/**
 * Country flag icon (2026-10-25).
 *
 * SVGs: country-flag-icons@1.6.20 (MIT, 3x2 set) copied to public/flags/<code>.svg —
 * one small file per flag, loaded by the browser only when shown (no bundle cost).
 * Never use emoji flags (Windows doesn't render them).
 *
 *   <CountryFlag code="US" />                 decorative, next to a visible name (aria-hidden)
 *   <CountryFlag code="US" standalone />      alone: alt = localized country name
 *   <CountryLabel code="US">United States</CountryLabel>
 *   <CountryLabel code="US" />                flag + localized name
 *   countryFromRegion('us-iad') -> 'US'       region slugs whose first segment is ISO alpha-2
 */
import React from 'react';
import { GlobalOutlined } from '@ant-design/icons';
import { Input } from 'antd';
import i18n from 'i18next';

// Codes available in public/flags (lowercase file names).
const FLAG_CODES = new Set(('ac ad ae af ag ai al am ao aq ar as at au aw ax az ba bb bd be bf bg bh bi bj bl bm bn bo bq-bo bq-sa bq-se bq br bs bt bv bw by bz ca cc cd cf cg ch ci ck cl cm cn co cr cu cv cw cx cy cz de dj dk dm do dz ec ee eg eh er es-ct es et eu fi fj fk fm fo fr ga gb-eng gb-nir gb-sct gb-wls gb gd ge gf gg gh gi gl gm gn gp gq gr gs gt gu gw gy hk hm hn hr ht hu ic id ie il im in io iq ir is it je jm jo jp ke kg kh ki km kn kp kr kw ky kz la lb lc li lk lr ls lt lu lv ly ma mc md me mf mg mh mk ml mm mn mo mp mq mr ms mt mu mv mw mx my mz na nc ne nf ng ni nl no np nr nu nz om pa pe pf pg ph pk pl pm pn pr ps pt pw py qa re ro rs ru rw sa sb sc sd se sg sh si sj sk sl sm sn so sr ss st sv sx sy sz ta tc td tf tg th tj tk tl tm tn to tr tt tv tw tz ua ug um us uy uz va vc ve vg vi vn vu wf ws xk ye yt za zm zw').split(' '));

// Non-ISO aliases seen in data / APIs.
const ALIASES = { uk: 'gb', el: 'gr' };

// Console language -> flag (zh-TW uses the Taiwan flag, owner decision for webmail; same here).
export const LANGUAGE_FLAGS = { 'zh-CN': 'CN', 'zh-TW': 'TW', en: 'US', es: 'ES', ja: 'JP', ko: 'KR', ms: 'MY', ru: 'RU' };

/** Normalized lowercase flag code or '' when we have no flag for it. */
export const normalizeCountryCode = (code) => {
    if (code === null || code === undefined) return '';
    let c = String(code).trim().toLowerCase().replace(/_/g, '-');
    if (!c) return '';
    c = ALIASES[c] || c;
    return FLAG_CODES.has(c) ? c : '';
};

/**
 * Country code from a region / location slug such as "us-iad", "de-fra-2", "gb-lon", "hk", "jp".
 * Only an exact ISO alpha-2 first segment counts; continent-ish slugs ("eu", "ap", "global") and
 * provider codes ("op-yul") give ''. Never guesses from free-text city names.
 */
export const countryFromRegion = (region) => {
    if (!region) return '';
    const s = String(region).trim().toLowerCase();
    const m = s.match(/^([a-z]{2})(?:$|[-_\s])/);
    if (!m || m[1] === 'eu') return '';
    const c = ALIASES[m[1]] || m[1];
    return FLAG_CODES.has(c) ? c.toUpperCase() : '';
};

const displayNamesCache = {};
/** Localized country name, or '' (EU / XK and unknown codes fall back to Intl's own output). */
export const countryName = (code, lang) => {
    const c = normalizeCountryCode(code);
    if (!c || c.includes('-')) return code ? String(code).toUpperCase() : '';
    const l = lang || i18n.language || 'en';
    try {
        if (!displayNamesCache[l]) displayNamesCache[l] = new Intl.DisplayNames([l, 'en'], { type: 'region' });
        return displayNamesCache[l].of(c.toUpperCase()) || c.toUpperCase();
    } catch (_) {
        return c.toUpperCase();
    }
};

const SIZES = { sm: [18, 12], md: [21, 14], lg: [27, 18] };

export default function CountryFlag({ code, size = 'sm', standalone = false, title, fallback = 'none', style, className }) {
    const c = normalizeCountryCode(code);
    const [w, h] = SIZES[size] || SIZES.sm;
    const base = { display: 'inline-block', flex: 'none', verticalAlign: '-0.15em', marginInlineEnd: 6, ...style };
    if (!c) {
        if (fallback !== 'globe') return null;
        return <GlobalOutlined aria-hidden="true" className={className} style={{ ...base, fontSize: h, width: w, color: 'var(--ant-color-text-tertiary, #999)' }} />;
    }
    const name = title || countryName(c);
    return (
        <img
            src={`${process.env.PUBLIC_URL || ''}/flags/${c}.svg`}
            width={w}
            height={h}
            loading="lazy"
            decoding="async"
            draggable={false}
            alt={standalone ? name : ''}
            aria-hidden={standalone ? undefined : 'true'}
            title={name}
            className={className}
            style={{ ...base, width: w, height: h, objectFit: 'cover', borderRadius: 2, boxShadow: '0 0 0 1px rgba(128,128,128,.25)' }}
        />
    );
}

/** Flag followed by a label (children, or the localized country name). */
export function CountryLabel({ code, children, size, fallback, style }) {
    const label = children !== undefined && children !== null && children !== '' ? children : countryName(code);
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', maxWidth: '100%', ...style }}>
            <CountryFlag code={code} size={size} fallback={fallback} />
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
        </span>
    );
}

/** Inline preview of country codes typed as free text ("CN\nRU", "US,GB" or an array): flag + code each. */
export function CountryFlagList({ value, style }) {
    const list = (Array.isArray(value) ? value : String(value ?? '').split(/[\s,，;；]+/))
        .map((x) => String(x).trim().toUpperCase()).filter((x) => normalizeCountryCode(x));
    if (!list.length) return null;
    return (
        <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '2px 10px', fontSize: 12, ...style }}>
            {[...new Set(list)].map((c) => <CountryLabel key={c} code={c}>{c}</CountryLabel>)}
        </span>
    );
}

/** Two-letter country code text input with the flag as prefix (Form.Item child: value/onChange injected).
 *  The prefix wrapper is always rendered: toggling antd's prefix remounts the input and drops focus. */
export function CountryCodeInput({ value, ...rest }) {
    return <Input value={value} prefix={<span style={{ display: 'inline-flex', minWidth: 18 }}><CountryFlag code={value} style={{ marginInlineEnd: 0 }} /></span>} {...rest} />;
}
